/**
 * API 客户端工具
 * 正式环境: arp-public-gateway.fengbao.com
 * 测试环境(dev-arp.fengbao.com) + 本地: dev-arp-public-gateway.fengbao.com
 */

const _host = location.hostname
const _isDev = _host === 'localhost' || _host === '127.0.0.1' || _host.startsWith('192.168.') || _host === 'dev-arp.fengbao.com' || _host === '8.219.1.89'
const _API_HOST = _isDev
  ? 'https://dev-arp-public-gateway.fengbao.com'
  : 'https://arp-public-gateway.fengbao.com'

const BASE_URL = `${_API_HOST}/api/v1`

// 前端版本号：UTC 年月日时分秒
const FRONTEND_VERSION = '20260605122156'

// 版本升级提示（全局只弹一次）
let _upgradeNotified = false

function getToken() {
  try {
    const raw = localStorage.getItem('mcn_auth')
    const state = raw ? JSON.parse(raw) : null
    return state ? state.token : null
  } catch {
    return null
  }
}

async function request(method, path, data = null, params = null) {
  const token = getToken()
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  // 每次请求生成唯一 Reqid（后端要求）
  const _reqid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
  headers['Reqid'] = _reqid
  headers['X-Frontend-Version'] = FRONTEND_VERSION

  // 路径以 /api/ 开头表示绝对路径，直接拼 host
  let url = path.startsWith('/api/') ? (_API_HOST + path) : (BASE_URL + path)
  if (params) {
    const entries = Object.entries(params).filter(([, v]) => v != null)
    if (entries.length) url += '?' + new URLSearchParams(Object.fromEntries(entries)).toString()
  }

  const options = { method, headers }
  if (data !== null) {
    headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(data)
  }

  let response
  try {
    response = await fetch(url, options)
  } catch (e) {
    throw new Error('网络请求失败，请检查网络连接')
  }

  // 检查版本升级
  const upgradeHeader = response.headers.get('X-Frontend-Upgrade')
  if (upgradeHeader === 'required' && !_upgradeNotified) {
    _upgradeNotified = true
    const latestVersion = response.headers.get('X-Frontend-Latest') || ''
    console.warn('[api] 前端版本需要更新:', FRONTEND_VERSION, '->', latestVersion)
    // 获取更新内容并通知前端展示弹窗
    _fetchLatestVersion().then(versionInfo => {
      window.dispatchEvent(new CustomEvent('frontend-upgrade', { detail: versionInfo }))
    })
  }

  if (response.status === 401) {
    localStorage.removeItem('mcn_auth')
    window.location.href = '/#/auth'
    return null
  }

  let json
  try {
    json = await response.json()
  } catch {
    // body 不是 JSON（如 HTTP 404 返 HTML 错误页）—— 也带上 HTTP status 让上层能精准判断
    const e = new Error('响应解析失败')
    e.status = response.status
    throw e
  }

  if (json.code === 200) return json.data

  // JSON body 里返回 401（token 失效）也跳登录页
  if (json.code === 401) {
    localStorage.removeItem('mcn_auth')
    window.location.href = '/#/auth'
    return null
  }

  // pending 用户(受限 token)调业务接口被后端拦 → code:403。
  // 仅当本地已知是 pending(入职审核中)时导回「等待审核」页，避免误把普通无权限当审核中。
  // 放行：注册 step1/step2、注册状态、登录、登出。
  if (json.code === 403) {
    let approvalStatus = ''
    try {
      const s = JSON.parse(localStorage.getItem('mcn_auth') || 'null')
      approvalStatus = s && s.approvalStatus
    } catch { /* ignore */ }
    const allowed = /\/auth\/register|\/auth\/login|\/auth\/logout|register\/status/.test(path)
    // 已在审核流程页(pending/rejected/employee/assistant)上,不再强制跳转,避免和页面自己的导航打架造成"闪一下"
    const onSetupPage = /\/setup\/(pending|rejected|employee|assistant)/.test(location.hash || '')
    if (approvalStatus === 'pending' && !allowed && !onSetupPage) {
      window.location.href = '/#/setup/pending'
      return null
    }
  }

  const err = new Error(json.message || json.msg || '请求失败')
  err.status = response.status              // HTTP 状态码
  err.responseData = json                   // 业务码 / 消息体
  throw err
}

// 获取最新版本信息（无需认证）
async function _fetchLatestVersion() {
  try {
    const res = await fetch(`${_API_HOST}/api/v1/web-versions/latest`)
    const json = await res.json()
    if (json.code === 200 && json.data) return json.data
  } catch (e) {
    console.warn('[api] 获取最新版本信息失败:', e.message)
  }
  return null
}

export { FRONTEND_VERSION, BASE_URL, getToken }

export const api = {
  // ── 认证接口 ──────────────────────────────────────────
  // POST /auth/login
  login: (username, password) =>
    request('POST', '/auth/login', { username, password }),

  // GET /api/v2/auth/register/status
  getRegisterStatus: () =>
    request('GET', '/api/v2/auth/register/status'),

  // POST /api/v2/auth/register/step1 - V2 注册一次性 账号+密码+企业(加入或创建)
  // mode='join' 必填 inviteCode；mode='create' 必填 companyName
  registerStep1: ({ username, password, mode, inviteCode, companyName }) => {
    const payload = { username, password, mode }
    if (mode === 'join') payload.inviteCode = inviteCode
    if (mode === 'create') payload.companyName = companyName
    return request('POST', '/api/v2/auth/register/step1', payload)
  },

  // GET /api/v2/positions - 获取岗位列表（无需认证；可选 departmentId 按部门筛）
  getPositions: (departmentId) => request('GET', '/api/v2/positions', null, departmentId ? { departmentId } : null),

  // GET /api/v2/positions/by-invite-code - 通过邀请码获取岗位列表（注册用，可选 departmentId）
  getPositionsByInviteCode: (code, departmentId) =>
    request('GET', '/api/v2/positions/by-invite-code', null, { code, ...(departmentId ? { departmentId } : {}) }),

  // GET /api/v2/departments/by-invite-code - 注册级联：通过邀请码取部门列表
  getDepartmentsByInviteCode: (code) =>
    request('GET', '/api/v2/departments/by-invite-code', null, { code }),

  // GET /api/v2/groups/by-invite-code - 注册级联：通过邀请码 + 部门取小组列表
  getGroupsByInviteCode: (code, departmentId) =>
    request('GET', '/api/v2/groups/by-invite-code', null, { code, ...(departmentId ? { departmentId } : {}) }),

  // POST /positions - 创建岗位
  // POST /api/v1/positions - 创建岗位（后端 POST 只持久化 name + departmentId；其余字段需创建后 PUT 补）
  createPosition: ({ name, description, departmentId, disabled, availableModels, defaultModel, availableDevModels, defaultDevModel } = {}) =>
    request('POST', '/positions', {
      name,
      ...(description != null ? { description } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(disabled != null ? { disabled } : {}),
      ...(availableModels != null ? { availableModels } : {}),
      ...(defaultModel != null ? { defaultModel } : {}),
      ...(availableDevModels != null ? { availableDevModels } : {}),
      ...(defaultDevModel != null ? { defaultDevModel } : {})
    }),

  // ── 企业 admin · 入职审核管理面（/api/v1/admin/*；仅 admin，只能审本团队）──
  // GET /api/v1/admin/approval-stats → { pending, todayNew, approvedTotal }
  getApprovalStats: () => request('GET', '/admin/approval-stats'),
  // GET /api/v1/admin/pending-members → { members:[{ id,username,name,avatarUrl,avatarColorHsl,positionId,positionName,groupId,groupName,departmentId,departmentName,createdAt }], total }
  getPendingMembers: () => request('GET', '/admin/pending-members'),
  // GET /api/v1/admin/approval-history → { members:[{...,result:'approved'|'rejected',reviewedAt}], total, approvedCount, rejectedCount }
  getApprovalHistory: () => request('GET', '/admin/approval-history'),
  // POST /api/v1/admin/members/:id/approve - 通过（可选归属微调；positionId/departmentId/groupId 三者要么都传要么都不传）
  approvePendingMember: (id, assign) => {
    const a = assign || {}
    const full = a.positionId && a.departmentId && a.groupId
    return request('POST', `/admin/members/${id}/approve`,
      full ? { positionId: a.positionId, departmentId: a.departmentId, groupId: a.groupId } : null)
  },
  // POST /api/v1/admin/members/:id/reject - 驳回
  rejectPendingMember: (id, reason) =>
    request('POST', `/admin/members/${id}/reject`, reason ? { reason } : null),
  // POST /api/v1/admin/members/:id/re-approve - 已拒绝重新通过(仅 rejected → approved;非 rejected 后端 400)
  reApprovePendingMember: (id) => request('POST', `/admin/members/${id}/re-approve`),

  // ── 企业 admin · 小组管理（/api/v1/groups；写操作仅 admin）──
  // GET /api/v1/groups?departmentId= - 小组列表 → { groups:[{id,name,departmentId,description,sortOrder,createdAt}] }
  getGroups: (departmentId) =>
    request('GET', '/groups', null, departmentId ? { departmentId } : null),
  getGroupDetail: (id) => request('GET', `/groups/${id}`),
  // POST /api/v1/groups - 新建（name 团队内唯一）
  createGroup: ({ name, departmentId, description, sortOrder } = {}) =>
    request('POST', '/groups', {
      name, departmentId,
      ...(description != null ? { description } : {}),
      ...(sortOrder != null ? { sortOrder } : {})
    }),
  // PUT /api/v1/groups/:id - 改名 / 改部门 / 描述 / 排序
  updateGroup: (id, { name, departmentId, description, sortOrder } = {}) =>
    request('PUT', `/groups/${id}`, {
      ...(name != null ? { name } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(description != null ? { description } : {}),
      ...(sortOrder != null ? { sortOrder } : {})
    }),
  // DELETE /api/v1/groups/:id - 删除（成员 group_id 置空）
  deleteGroup: (id) => request('DELETE', `/groups/${id}`),
  // PUT /api/v1/groups/:id/status - 启用/停用（决定B,待后端补）
  updateGroupStatus: (id, status) => request('PUT', `/groups/${id}/status`, { status }),
  // PUT /api/v1/departments/:id/status - 启用/停用（仅 admin）
  updateDepartmentStatus: (id, status) => request('PUT', `/departments/${id}/status`, { status }),

  // ── 企业 admin · 成员归属 / 在职离职 ──
  // PUT /api/v1/admin/members/:id/placement - 改岗位/部门/小组（positionId 必填）
  updateMemberPlacement: (id, { positionId, departmentId, groupId } = {}) =>
    request('PUT', `/admin/members/${id}/placement`, {
      positionId,
      ...(departmentId ? { departmentId } : {}),
      ...(groupId ? { groupId } : {})
    }),
  // PUT /api/v1/team/members/:id/employment - 在职 active / 离职 left
  updateMemberEmployment: (id, employment) =>
    request('PUT', `/team/members/${id}/employment`, { employment }),

  // PUT /api/v2/positions/:id - 编辑岗位（改名/描述/部门/停用/模型；全部可选；无删除）
  updatePosition: (id, { name, description, departmentId, disabled, availableModels, defaultModel, availableDevModels, defaultDevModel } = {}) =>
    request('PUT', `/api/v2/positions/${id}`, {
      ...(name != null ? { name } : {}),
      ...(description != null ? { description } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(disabled != null ? { disabled } : {}),
      ...(availableModels != null ? { availableModels } : {}),
      ...(defaultModel != null ? { defaultModel } : {}),
      ...(availableDevModels != null ? { availableDevModels } : {}),
      ...(defaultDevModel != null ? { defaultDevModel } : {})
    }),

  // POST /api/v2/auth/register/step2 - V2 注册第二步：部门+小组+岗位+姓名
  //   create 模式 departmentId/groupId 可空；join 模式必填
  registerStep2: ({ positionId, name, departmentId, groupId }) =>
    request('POST', '/api/v2/auth/register/step2', {
      positionId,
      name,
      ...(departmentId ? { departmentId } : {}),
      ...(groupId ? { groupId } : {})
    }),

  // POST /auth/register
  // teamMode: 'create' 时 teamName 必填，'join' 时 inviteCode 必填
  register: ({ username, password, email, displayName, teamMode, teamName, inviteCode }) =>
    request('POST', '/auth/register', {
      username,
      password,
      email,
      ...(displayName ? { display_name: displayName } : {}),
      team_mode: teamMode,
      ...(teamName ? { team_name: teamName } : {}),
      ...(inviteCode ? { invite_code: inviteCode } : {})
    }),

  // POST /auth/logout
  logout: () => request('POST', '/auth/logout'),

  // GET /unread-count
  getUnreadCount: () => request('GET', '/unread-count'),

  // ── 用户接口 ──────────────────────────────────────────
  // GET /users/me
  getMe: () => request('GET', '/users/me'),

  // PUT /users/me
  updateMe: (data) => request('PUT', '/users/me', data),

  // GET /users
  getUsers: () => request('GET', '/users'),

  // GET /users/:id
  getUserById: (id) => request('GET', `/users/${id}`),

  // GET /users/search?keyword=xxx
  searchUsers: (keyword) => request('GET', '/users/search', null, { keyword }),

  // ── 联系人接口 ────────────────────────────────────────
  // GET /contacts  (type 可选: account / agent)
  getContacts: (type) => request('GET', '/contacts', null, type ? { type } : {}),

  // ── 团队接口 ──────────────────────────────────────────
  // GET /team
  getTeam: () => request('GET', '/team'),

  // PUT /team - 修改团队信息（name, avatarUrl 至少传一个）
  updateTeam: ({ name, avatarUrl } = {}) =>
    request('PUT', '/team', { ...(name ? { name } : {}), ...(avatarUrl ? { avatarUrl } : {}) }),

  // ── 设置接口 ──────────────────────────────────────────
  // GET /settings - 获取用户全部设置
  getSettings: () => request('GET', '/settings'),
  // PUT /settings - 更新用户设置（部分更新）
  updateSettings: (data) => request('PUT', '/settings', data),
  // PUT /settings/password - 修改登录密码
  changePassword: ({ oldPassword, newPassword }) =>
    request('PUT', '/settings/password', { oldPassword, newPassword }),
  // PUT /settings/position - 修改自己的岗位
  changePosition: ({ positionId }) =>
    request('PUT', '/settings/position', { positionId }),
  // GET /settings/notifications - 获取通知设置
  getNotificationSettings: () => request('GET', '/settings/notifications'),
  // PUT /settings/notifications - 更新通知设置
  updateNotificationSettings: (data) => request('PUT', '/settings/notifications', data),
  // GET /settings/general - 获取通用设置
  getGeneralSettings: () => request('GET', '/settings/general'),
  // PUT /settings/general - 更新通用设置
  updateGeneralSettings: (data) => request('PUT', '/settings/general', data),
  // POST /settings/export - 导出用户数据
  exportData: (types) => request('POST', '/settings/export', types ? { types } : {}),

  // ── 应用中心 ────────────────────────────────────────
  // GET /api/v1/app-center - 列表（tab: enterprise|starred|mine；departmentId 仅 enterprise 生效）
  getAppCenterList: ({ tab, departmentId, keyword, sortBy, order, page, pageSize } = {}) =>
    request('GET', '/api/v1/app-center', null, {
      tab: tab || 'enterprise',
      ...(departmentId ? { departmentId } : {}),
      ...(keyword ? { keyword } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(order ? { order } : {}),
      page: page || 1,
      pageSize: pageSize || 20
    }),
  starApp: (appId) => request('POST', `/api/v1/app-center/${appId}/star`),
  unstarApp: (appId) => request('DELETE', `/api/v1/app-center/${appId}/star`),
  unpublishApp: (appId) => request('POST', `/api/v1/app-center/${appId}/unpublish`),
  deleteAppCenterApp: (appId) => request('DELETE', `/api/v1/app-center/${appId}`),
  updateAppVisibility: (appId, visibility) =>
    request('PUT', `/api/v1/app-center/${appId}/visibility`, { visibility }),

  // ── 天玑（方片 fp_）账号绑定 ────────────────────────
  // PUT /tianji/credential - 绑定/重新绑定天玑账号（含 live 验证：login → userInfo → 拉 list 拿可见量）
  // 任一步失败直接 400，不入库；返回 { credential, preview }
  bindTianjiCredential: ({ username, password }) =>
    request('PUT', '/tianji/credential', { username, password }),
  // DELETE /tianji/credential - 解绑天玑账号（凭证置 revoked、清 cached_token、引用 agent/app 立即 401）
  // 幂等：重复调用返 200；想恢复必须重新走 PUT
  unbindTianjiCredential: (reason) =>
    request('DELETE', `/tianji/credential${reason ? '?reason=' + encodeURIComponent(reason) : ''}`),

  // ── Web Push 接口 ──────────────────────────────────────
  getVapidPublicKey: async () => {
    const token = getToken()
    const h = {}
    if (token) h['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${_API_HOST}/api/v1/push/vapid-public-key`, { headers: h })
    return res.json()
  },
  subscribePush: async ({ endpoint, authKey, p256dhKey, deviceId }) => {
    const token = getToken()
    const h = { 'Content-Type': 'application/json' }
    if (token) h['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${_API_HOST}/api/v1/push/subscribe`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ endpoint, authKey, p256dhKey, ...(deviceId ? { deviceId } : {}) })
    })
    return res.json()
  },
  unsubscribePush: async (endpoint) => {
    const token = getToken()
    const h = { 'Content-Type': 'application/json' }
    if (token) h['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${_API_HOST}/api/v1/push/subscribe`, {
      method: 'DELETE', headers: h, body: JSON.stringify({ endpoint })
    })
    return res.json()
  },

  // GET /team/members
  getTeamMembers: ({ departmentId, keyword } = {}) =>
    request('GET', '/team/members', null, {
      ...(departmentId ? { departmentId } : {}),
      ...(keyword ? { keyword } : {})
    }),

  // GET /team/members/:id
  getTeamMemberDetail: (id) => request('GET', `/team/members/${id}`),

  // ── 部门接口 ──────────────────────────────────────────
  getDepartments: () => request('GET', '/departments'),
  // POST /api/v1/departments - 创建部门(前端一次性发完整字段)
  createDepartment: ({ name, description, positionIds, leaderId } = {}) =>
    request('POST', '/departments', {
      name,
      ...(description != null ? { description } : {}),
      ...(positionIds ? { positionIds } : {}),
      ...(leaderId !== undefined ? { leaderId } : {})
    }),
  getDepartmentDetail: (id) => request('GET', `/departments/${id}`),
  // PUT /api/v1/departments/:id - 改名/描述/部门负责人/岗位绑定（仅 admin；leaderId: ''=清除 / id=设置 / undefined=不动)
  updateDepartment: (id, { name, description, positionIds, leaderId } = {}) =>
    request('PUT', `/departments/${id}`, {
      ...(name != null ? { name } : {}),
      ...(description != null ? { description } : {}),
      ...(positionIds ? { positionIds } : {}),
      ...(leaderId !== undefined ? { leaderId } : {})
    }),
  deleteDepartment: (id) => request('DELETE', `/departments/${id}`),
  // GET /api/v1/enterprise/models - 企业授权模型白名单（真实模型 ID 数组）
  getEnterpriseModels: () => request('GET', '/enterprise/models'),

  // GET /api/v1/enterprise/today-usage - 企业概况「使用情况」: activeMembers(本月累计)+ todayActiveUsers + todayAgentCalls
  getEnterpriseTodayUsage: () => request('GET', '/enterprise/today-usage'),

  // ── 企业 admin · 概览 P3（/api/v1/enterprise/*；全 admin）──
  getAgentDistribution: () => request('GET', '/enterprise/agent-distribution'),
  // GET /api/v1/enterprise/token-trend?range=day|week|month → { labels, data, unit, sub }
  getTokenTrend: (range) => request('GET', '/enterprise/token-trend', null, range ? { range } : null),
  // GET /api/v1/enterprise/api-consumption?range= → { currentCalls,currentTokens,deltaTokens,deltaPercent,totalTokens,labels,data,unit,sub }
  getApiConsumption: (range) => request('GET', '/enterprise/api-consumption', null, range ? { range } : null),
  getAssetTiles: () => request('GET', '/enterprise/asset-tiles'),
  getMembersRanking: (range) => request('GET', '/enterprise/members/ranking', null, range ? { range } : null),
  getDepartmentsRanking: (range) => request('GET', '/enterprise/departments/ranking', null, range ? { range } : null),
  getAppsRanking: (sort) => request('GET', '/enterprise/apps/ranking', null, sort ? { sort } : null),
  getSkillsRanking: (sort) => request('GET', '/enterprise/skills/ranking', null, sort ? { sort } : null),
  // 成员详情(P3)
  getMemberStats: (id) => request('GET', `/enterprise/members/${id}/stats`),
  getMemberApps: (id) => request('GET', `/enterprise/members/${id}/apps`),
  // PUT /api/v1/team/members/:id - 弹窗保存（仅 admin；name/avatarUrl/password/部门/小组/岗位/模型；无 phone）
  updateMember: (id, { name, password, avatarUrl, departmentId, groupId, positionId, availableModels, defaultModel, availableDevModels, defaultDevModel } = {}) =>
    request('PUT', `/team/members/${id}`, {
      ...(name != null ? { name } : {}),
      ...(password ? { password } : {}),
      ...(avatarUrl != null ? { avatarUrl } : {}),
      ...(departmentId != null ? { departmentId } : {}),
      ...(groupId != null ? { groupId } : {}),
      ...(positionId != null ? { positionId } : {}),
      ...(availableModels != null ? { availableModels } : {}),
      ...(defaultModel != null ? { defaultModel } : {}),
      ...(availableDevModels != null ? { availableDevModels } : {}),
      ...(defaultDevModel != null ? { defaultDevModel } : {})
    }),

  // ── 智能体接口 ────────────────────────────────────────
  // POST /conversations/:id/leave
  leaveConversation: (conversationId) => request('POST', `/conversations/${conversationId}/leave`),
  dismissConversation: (conversationId) => request('POST', `/conversations/${conversationId}/dismiss`),

  // 云电脑操作
  restartOpenClaw: (agentId, vncDomain) => request('POST', `/agents/${agentId}/restart-oc`, { vnc_domain: vncDomain }),
  rescueOpenClaw: (agentId, vncDomain) => request('POST', `/agents/${agentId}/rescue-oc`, { vnc_domain: vncDomain }),

  // GET /agent-roles
  getAgentRoles: () => request('GET', '/agent-roles'),
  getSkillCategories: () => request('GET', '/skills/categories'),
  // POST /skills/publish — 发布/更新 Skill
  publishSkill: (body) => request('POST', '/skills/publish', body),
  listSkillOnEnterprise: (id) => request('POST', `/skills/${id}/list-on-enterprise`),
  unlistSkillFromEnterprise: (id) => request('POST', `/skills/${id}/unlist-from-enterprise`),
  getAgentRoleDetail: (id) => request('GET', `/agent-roles/${id}`),

  // GET /agents
  getAgents: () => request('GET', '/agents'),

  // ── Agent 市场 ──
  // GET /agents/enterprise - 企业 Agent 列表
  listEnterpriseAgents: ({ keyword, limit, offset } = {}) =>
    request('GET', '/agents/enterprise', null, {
      ...(keyword ? { keyword } : {}),
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {})
    }),
  // GET /agents/enterprise/:id - 企业 Agent 详情
  getEnterpriseAgentDetail: (id) => request('GET', `/agents/enterprise/${id}`),
  // POST /agents/:id/clone - 复制 Agent 到当前用户
  cloneAgent: (id, body) => request('POST', `/agents/${id}/clone`, body || {}),
  // POST /agents/:id/list-on-enterprise - 上架到企业市场
  listAgentOnEnterprise: (id) => request('POST', `/agents/${id}/list-on-enterprise`),
  // POST /agents/:id/unlist-from-enterprise - 下架
  unlistAgentFromEnterprise: (id) => request('POST', `/agents/${id}/unlist-from-enterprise`),

  // GET /conversations/:id/models
  getConversationModels: (conversationId) => request('GET', `/conversations/${conversationId}/models`),

  // GET /scheduled-tasks
  getScheduledTasks: ({ agent_id, role_id, search, status, sort_by, sort_dir, limit, offset } = {}) =>
    request('GET', '/scheduled-tasks', null, {
      ...(agent_id ? { agent_id } : {}),
      ...(role_id ? { role_id } : {}),
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(sort_by ? { sort_by } : {}),
      ...(sort_dir ? { sort_dir } : {}),
      ...(limit ? { limit } : {}),
      ...(offset != null ? { offset } : {})
    }),

  // POST /agents/:id/scheduled-tasks/:jobId/disable
  disableScheduledTask: (agentId, jobId) =>
    request('POST', `/agents/${agentId}/scheduled-tasks/${jobId}/disable`),

  // POST /agents/:id/scheduled-tasks/:jobId/enable
  enableScheduledTask: (agentId, jobId) =>
    request('POST', `/agents/${agentId}/scheduled-tasks/${jobId}/enable`),

  getSkills: ({ scope, page, pageSize, categoryId, roleId, keyword, sortBy, order, departmentId } = {}) =>
    request('GET', '/skills', null, {
      scope: scope || 'public',
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(roleId ? { roleId } : {}),
      ...(keyword ? { keyword } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(order ? { order } : {}),
      ...(departmentId ? { departmentId } : {})
    }),

  // GET /skills/:id/agents
  getSkillAgents: (skillId) => request('GET', `/skills/${skillId}/agents`),

  // GET /skills/:id/changelogs — 技能各版本更新日志（最新在前），返回 { skillId, total, changelogs:[{version,publishedAt,changelog}] }
  getSkillVersions: (skillId) => request('GET', `/skills/${skillId}/changelogs`),

  // POST /skills/batch-install
  batchInstallSkill: (skillId, agentIds) => request('POST', '/skills/batch-install', { skillId, agentIds }),

  // POST /agents/:id/skills/install — 为单个 Agent 安装/更新一个 Skill
  // 若该 skill 在 Agent 本地已存在=「更新」，带 conversationId+topicId 时后端更新成功后会往该会话推"技能更新成功"通知
  installAgentSkill: (agentId, { skillId, conversationId, topicId } = {}) =>
    request('POST', `/agents/${agentId}/skills/install`, {
      skillId,
      ...(conversationId ? { conversationId } : {}),
      ...(topicId ? { topicId } : {})
    }),

  // GET /agent-files
  getAgentFiles: ({ agent_id, job_id, file_type, role_id, search, sort_by, sort_dir, limit, offset } = {}) =>
    request('GET', '/agent-files', null, {
      ...(agent_id ? { agent_id } : {}),
      ...(job_id ? { job_id } : {}),
      ...(file_type ? { file_type } : {}),
      ...(role_id ? { role_id } : {}),
      ...(search ? { search } : {}),
      ...(sort_by ? { sort_by } : {}),
      ...(sort_dir ? { sort_dir } : {}),
      ...(limit ? { limit } : {}),
      ...(offset != null ? { offset } : {})
    }),

  // GET /agents/capabilities?agent_id=xxx
  getAgentCapabilities: (agentId) => request('GET', '/agents/capabilities', null, { agent_id: agentId }),

  // POST /agents - 公共角色"添加"（从既有 agent_role 派生）
  createAgent: ({ name, roleId } = {}) =>
    request('POST', '/agents', {
      name,
      ...(roleId ? { roleId } : {})
    }),

  // GET /agents/create-defaults - 新建 Agent 弹窗的默认填充（模型列表 + 默认 role 模板 + 预装 skill）
  getCreateDefaults: () => request('GET', '/agents/create-defaults'),

  // POST /agents/from-scratch - 从零创建 Agent（自动 mint 一个 private role + agent）
  createAgentFromScratch: ({
    name, description, avatarType, avatarUrl, avatarEmoji, avatarColorHsl, avatarColorRgb,
    roleName, visibility, model, skillIds,
    identity, soul, persona, style, system, memory
  } = {}) =>
    request('POST', '/agents/from-scratch', {
      name,
      roleName,
      ...(description ? { description } : {}),
      ...(avatarType ? { avatarType } : {}),
      ...(avatarUrl ? { avatarUrl } : {}),
      ...(avatarEmoji ? { avatarEmoji } : {}),
      ...(avatarColorHsl ? { avatarColorHsl } : {}),
      ...(avatarColorRgb ? { avatarColorRgb } : {}),
      ...(visibility ? { visibility } : {}),
      ...(model ? { model } : {}),
      ...(skillIds && skillIds.length ? { skillIds } : {}),
      ...(identity ? { identity } : {}),
      ...(soul ? { soul } : {}),
      ...(persona ? { persona } : {}),
      ...(style ? { style } : {}),
      ...(system ? { system } : {}),
      ...(memory ? { memory } : {})
    }),

  // GET /agents/:id/scheduled-tasks
  getAgentScheduledTasks: (agentId, { conversationId, topicId } = {}) =>
    request('GET', `/agents/${agentId}/scheduled-tasks`, null, {
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(topicId ? { topic_id: topicId } : {})
    }),

  // GET /agents/:id/scheduled-tasks/:jobId
  getAgentScheduledTaskDetail: (agentId, jobId) =>
    request('GET', `/agents/${agentId}/scheduled-tasks/${jobId}`),

  // GET /agents/:id/prompt-sections
  getAgentPromptSections: (agentId) => request('GET', `/agents/${agentId}/prompt-sections`),

  // PATCH /agents/:id/prompt-sections
  updateAgentPromptSections: (agentId, sections) => request('PATCH', `/agents/${agentId}/prompt-sections`, sections),

  // POST /agents/:id/prompt-sections/reset
  resetAgentPromptSection: (agentId, section) => request('POST', `/agents/${agentId}/prompt-sections/reset`, { section }),

  // GET /agents/:id
  getAgent: (id) => request('GET', `/agents/${id}`),

  // PUT /agents/:id
  updateAgent: (id, data) => request('PUT', `/agents/${id}`, data),

  // DELETE /agents/:id
  deleteAgent: (id) => request('DELETE', `/agents/${id}`),

  // WeChat binding
  getAgentWechat: (agentId) => request('GET', `/agents/${agentId}/wechat`),
  deleteAgentWechat: (agentId) => request('DELETE', `/agents/${agentId}/wechat`),
  postAgentWechatQrcode: (agentId) => request('POST', `/agents/${agentId}/wechat/qrcode`),

  // GET /servers
  getAgentServer: () => request('GET', '/servers'),

  // GET /conversations/:id/agents/available
  getAvailableAgentsForConversation: (conversationId) =>
    request('GET', `/conversations/${conversationId}/agents/available`),

  // POST /conversations/:id/agents
  addAgentsToConversation: (conversationId, agentIds) =>
    request('POST', `/conversations/${conversationId}/agents`, { agent_ids: agentIds }),

  // DELETE /conversations/:id/agents/:agentId
  kickAgentFromConversation: (conversationId, agentId) =>
    request('DELETE', `/conversations/${conversationId}/agents/${agentId}`),

  // DELETE /conversations/:id/members/:userId
  kickMemberFromConversation: (conversationId, userId) =>
    request('DELETE', `/conversations/${conversationId}/members/${userId}`),

  // GET /conversations/:id/agents
  getConversationAgents: (conversationId) =>
    request('GET', `/conversations/${conversationId}/agents`),

  // ── 好友 / 联系人接口 ──────────────────────────────────
  // POST /friends/request
  sendFriendRequest: (toUserId, message) =>
    request('POST', '/friends/request', {
      toUserId,
      ...(message ? { message } : {})
    }),

  // GET /friends/requests
  getFriendRequests: () => request('GET', '/friends/requests'),

  // POST /friends/requests/:id  action: 'accept' | 'reject'
  handleFriendRequest: (id, action) =>
    request('POST', `/friends/requests/${id}`, { action }),

  // PUT /friends/:id/remark
  setFriendRemark: (id, remark) =>
    request('PUT', `/friends/${id}/remark`, { remark }),

  // GET /friends/:id/online - 查询好友在线状态
  getFriendOnline: (id) => request('GET', `/friends/${id}/online`),

  // DELETE /friends/:id
  deleteFriend: (id) => request('DELETE', `/friends/${id}`),

  // ── 会话接口 ──────────────────────────────────────────
  // GET /conversations
  getConversations: () => request('GET', '/conversations'),
  getOnlineCount: (conversationId) => request('GET', `/conversations/${conversationId}/online-count`),

  // POST /conversations  type: 'direct' | 'group'
  createConversation: ({ type, name, participantIds, agentIds }) =>
    request('POST', '/conversations', {
      type,
      ...(name ? { name } : {}),
      ...(participantIds && participantIds.length ? { participant_ids: participantIds } : {}),
      ...(agentIds && agentIds.length ? { agent_ids: agentIds } : {})
    }),

  // GET /conversations/:id/members
  getGroupMembers: (conversationId) =>
    request('GET', `/conversations/${conversationId}/members`),

  // POST /conversations/:id/members
  addMemberToConversation: (conversationId, userId) =>
    request('POST', `/conversations/${conversationId}/members`, { user_id: userId }),

  // GET /conversations/:id/members/available
  getAvailableMembers: (conversationId) =>
    request('GET', `/conversations/${conversationId}/members/available`),

  // POST /conversations/:id/invite
  inviteGroupMembers: (conversationId, userIds) =>
    request('POST', `/conversations/${conversationId}/invite`, { userIds }),

  // PUT /topics/:id/read
  markTopicRead: (topicId) =>
    request('PUT', `/topics/${topicId}/read`),

  // ── 消息接口 ──────────────────────────────────────────
  // GET /conversations/:id/messages  (topicId 可选)
  getMessages: (conversationId, topicId, { limit, offset, around, after, before } = {}) =>
    request('GET', `/conversations/${conversationId}/messages`, null, {
      limit: limit || 50,
      ...(offset ? { offset } : {}),
      ...(topicId ? { topic_id: topicId } : {}),
      ...(around ? { around } : {}),
      ...(after ? { after } : {}),
      ...(before ? { before } : {})
    }),

  // ── 消息置顶（topic 级）───────────────────────────────
  // GET /conversations/:id/pinned-messages?topic_id=
  getPinnedMessages: (conversationId, topicId) =>
    request('GET', `/conversations/${conversationId}/pinned-messages`, null, topicId ? { topic_id: topicId } : {}),

  // POST /conversations/:id/messages/:mid/pin   body: { topic_id? }
  pinMessage: (conversationId, messageId, topicId) =>
    request('POST', `/conversations/${conversationId}/messages/${messageId}/pin`, topicId ? { topic_id: topicId } : {}),

  // DELETE /conversations/:id/messages/:mid/pin?topic_id=
  unpinMessage: (conversationId, messageId, topicId) =>
    request('DELETE', `/conversations/${conversationId}/messages/${messageId}/pin`, null, topicId ? { topic_id: topicId } : {}),

  // GET /messages/search  全局/会话内消息搜索（支持话题维度）
  searchMessages: ({ keyword, conversationId, topicId, messageType, date, startDate, endDate, limit, offset } = {}) =>
    request('GET', '/messages/search', null, {
      ...(keyword ? { keyword } : {}),
      ...(conversationId ? { conversationId } : {}),
      ...(topicId ? { topicId } : {}),
      ...(messageType ? { messageType } : {}),
      ...(date ? { date } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      limit: limit || 20,
      ...(offset ? { offset } : {})
    }),

  // GET /messages/:id/readers — 已读/未读人员列表
  getMessageReaders: (messageId) =>
    request('GET', `/messages/${messageId}/readers`),

  // GET /conversations/:id/agent-messages  (Agent 思考过程)
  getAgentMessages: (conversationId, { topicId, limit, offset } = {}) =>
    request('GET', `/conversations/${conversationId}/agent-messages`, null, {
      ...(topicId ? { topic_id: topicId } : {}),
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {})
    }),

  // GET /conversations/:id/topics/:topicId/group-plans  (群聊任务计划)
  getGroupPlans: (conversationId, topicId) =>
    request('GET', `/conversations/${conversationId}/topics/${topicId}/group-plans`),

  // POST /conversations/:id/messages  (文本消息)
  sendMessage: (conversationId, { text, topicId, mentions, model, think, skills, replyToId }) =>
    request('POST', `/conversations/${conversationId}/messages`, {
      text,
      messageType: 'text',
      ...(topicId ? { topicId } : {}),
      ...(replyToId ? { replyToId } : {}),
      ...(mentions && mentions.length ? { mentions } : {}),
      ...(model ? { model } : {}),
      ...(think ? { think } : {}),
      ...(skills && skills.length ? { skills } : {})
    }),

  // POST /conversations/:id/messages  (媒体消息)
  sendMediaMessage: (conversationId, { messageType, body, topicId, mentions, replyToId }) =>
    request('POST', `/conversations/${conversationId}/messages`, {
      messageType,
      body,
      ...(topicId ? { topicId } : {}),
      ...(mentions && mentions.length ? { mentions } : {}),
      ...(replyToId ? { replyToId } : {})
    }),

  // GET /conversations/:id/mentionable
  getMentionableMembers: (conversationId) =>
    request('GET', `/conversations/${conversationId}/mentionable`),

  // ── 话题接口 ──────────────────────────────────────────
  // GET /conversations/:id/topics
  getTopics: (conversationId) =>
    request('GET', `/conversations/${conversationId}/topics`),

  // GET /topics/:id/folders/tree
  getTopicFolderTree: (topicId) =>
    request('GET', `/topics/${topicId}/folders/tree`),

  // POST /conversations/:id/topics
  createTopic: (conversationId, name) =>
    request('POST', `/conversations/${conversationId}/topics`, { name }),

  // PUT /conversations/:id
  updateConversation: (conversationId, data) =>
    request('PUT', `/conversations/${conversationId}`, data),

  // PUT /conversations/:id/topics/:topicId
  updateTopic: (conversationId, topicId, data) =>
    request('PUT', `/conversations/${conversationId}/topics/${topicId}`, data),

  // DELETE /conversations/:id/topics/:topicId
  deleteTopic: (conversationId, topicId) =>
    request('DELETE', `/conversations/${conversationId}/topics/${topicId}`),

  // POST /conversations/:id/topics/:topicId/close
  closeTopic: (conversationId, topicId) =>
    request('POST', `/conversations/${conversationId}/topics/${topicId}/close`),

  // POST /conversations/:id/topics/:topicId/reopen
  reopenTopic: (conversationId, topicId) =>
    request('POST', `/conversations/${conversationId}/topics/${topicId}/reopen`),

  // ── 上传接口（预签名方式） ──────────────────────────────
  // 1. 获取预签名 URL
  getPresignUrl: ({ fileName, contentType, fileSize }) =>
    request('POST', '/files/presign', {
      file_name: fileName,
      ...(contentType ? { content_type: contentType } : {}),
      ...(fileSize ? { file_size: fileSize } : {})
    }),

  // 分片上传（大文件 OSS Multipart）—— 契约见 plan，后端就绪后接口通即可用
  // 初始化分片 → 返 { uploadId, objectKey }
  initMultipart: ({ fileName, contentType, fileSize }) =>
    request('POST', '/upload/multipart/init', { fileName, contentType, fileSize }),
  // 给某一片签名 → 返 { uploadUrl, method }
  getMultipartPartUrl: ({ objectKey, uploadId, partNumber }) =>
    request('POST', '/upload/multipart/part-url', { objectKey, uploadId, partNumber }),
  // 合并 → 返 { accessUrl, objectKey, fileName, fileSize, mimeType }
  // fileName/fileSize/contentType 由前端回传用于回显（合并接口本身不掌握）
  completeMultipart: ({ objectKey, uploadId, parts, fileName, fileSize, contentType }) =>
    request('POST', '/upload/multipart/complete', {
      objectKey, uploadId, parts,
      ...(fileName ? { fileName } : {}),
      ...(fileSize ? { fileSize } : {}),
      ...(contentType ? { contentType } : {})
    }),
  // 中止（取消/失败清理碎片）
  abortMultipart: ({ objectKey, uploadId }) =>
    request('POST', '/upload/multipart/abort', { objectKey, uploadId }),

  // 2. 通过预签名 URL 上传文件到 OSS，返回 accessUrl
  // 大于 5MB 走分片上传（切片 + 逐片重试 + 停顿检测）；否则单次 PUT。两条路返回结构一致。
  uploadImage: async (file, onProgress) => {
    if (file && file.size > 5 * 1024 * 1024) {
      const { multipartUpload } = require('./multipartUpload')
      return multipartUpload(file, onProgress)
    }
    return api._presignUpload(file, onProgress)
  },

  uploadFile: async (file, onProgress) => {
    if (file && file.size > 5 * 1024 * 1024) {
      const { multipartUpload } = require('./multipartUpload')
      return multipartUpload(file, onProgress)
    }
    return api._presignUpload(file, onProgress)
  },

  // POST /upload/avatar — 用户头像上传（multipart, 字段名 file）→ { url }
  uploadAvatar: async (file) => {
    const fd = new FormData()
    fd.append('file', file)
    const token = getToken()
    const reqid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
    })
    const headers = { Reqid: reqid, 'X-Frontend-Version': FRONTEND_VERSION }
    if (token) headers['Authorization'] = `Bearer ${token}`
    let res
    try { res = await fetch(`${BASE_URL}/upload/avatar`, { method: 'POST', headers, body: fd }) }
    catch (e) { throw new Error('网络请求失败，请检查网络连接') }
    const json = await res.json().catch(() => null)
    if (json && json.code === 200) return json.data  // { url }
    throw new Error((json && (json.message || json.msg)) || '头像上传失败')
  },

  // 内部：预签名上传流程，支持 onProgress(percent) 回调
  _presignUpload: async (file, onProgress) => {
    const presign = await api.getPresignUrl({
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSize: file.size
    })
    const data = presign && presign.data ? presign.data : presign
    if (!data || !data.uploadUrl) throw new Error('获取上传地址失败')

    // 用 XHR 替换 fetch，支持上传进度
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(data.method || 'PUT', data.uploadUrl)
      xhr.setRequestHeader('Content-Type', data.contentType || file.type || 'application/octet-stream')
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round(e.loaded / e.total * 100))
        }
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve()
        else reject(new Error(`上传失败: ${xhr.status}`))
      }
      xhr.onerror = () => reject(new Error('网络错误'))
      xhr.ontimeout = () => reject(new Error('上传超时'))
      xhr.timeout = 5 * 60 * 1000 // 5 分钟
      xhr.send(file)
    })

    return {
      url: data.accessUrl,
      objectKey: data.objectKey,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream'
    }
  },

  // ── 文件接口 ──────────────────────────────────────────
  // GET /conversations/:id/files  (topic_id 可选)
  getConversationFiles: (conversationId, { topicId, type, folderId, page = 1, pageSize = 50, sortBy = 'created_at', sortOrder = 'desc' } = {}) =>
    request('GET', `/conversations/${conversationId}/files`, null, {
      ...(topicId    ? { topic_id:   topicId }   : {}),
      ...(type       ? { type }                  : {}),
      ...(folderId   ? { folder_id:  folderId }  : {}),
      page,
      page_size: pageSize,
      sort_by:    sortBy,
      sort_order: sortOrder
    }),

  // GET /conversations/:id/topics/:topicId/images —— 按话题取聊天图片（单张粒度，游标分页）
  // 三种加载模式：anchorMessageId(+anchorFileIndex) 初次开窗 / beforeSeq 向更早 / afterSeq 向更新
  // 返回 data: { items:[{messageId,fileIndex,url,fileName,width,height,seq,createdAt}], total, anchorIndex, hasOlder, hasNewer }
  // items 始终 seq ASC, fileIndex ASC；翻页以消息为粒度（多图消息不拆页），limit 为近似上限
  getTopicImages: (conversationId, topicId, { limit = 30, anchorMessageId, anchorFileIndex, beforeSeq, afterSeq } = {}) =>
    request('GET', `/conversations/${conversationId}/topics/${topicId}/images`, null, {
      limit,
      ...(anchorMessageId != null ? { anchorMessageId, anchorFileIndex: anchorFileIndex || 0 } : {}),
      ...(beforeSeq != null ? { beforeSeq } : {}),
      ...(afterSeq  != null ? { afterSeq }  : {})
    }),

  // GET /files/:id
  getFile: (fileId) => request('GET', `/files/${fileId}`),

  // GET /files/:id/versions
  getFileVersions: (fileId, { conversationId, topicId } = {}) =>
    request('GET', `/files/${fileId}/versions`, null, {
      conversation_id: conversationId,
      ...(topicId ? { topic_id: topicId } : {})
    }),

  // ── 协作空间 Teable ─────────────────────────────────────
  // POST /teable/ensure-space — 团队开通 Teable Space（幂等）
  ensureTeableSpace: () => request('POST', '/teable/ensure-space', {}),

  // ── 协作空间 文件夹 ─────────────────────────────────────
  listCollabFolders: () => request('GET', '/collab/folders'),
  createCollabFolder: ({ parentId, name, visibility } = {}) =>
    request('POST', '/collab/folders', {
      ...(parentId ? { parentId } : {}),
      name,
      ...(visibility ? { visibility } : {})
    }),
  updateCollabFolder: (id, { name, parentId, sortOrder, visibility } = {}) =>
    request('PUT', `/collab/folders/${id}`, {
      ...(name !== undefined ? { name } : {}),
      ...(parentId !== undefined ? { parentId } : {}),
      ...(sortOrder !== undefined ? { sortOrder } : {}),
      ...(visibility !== undefined ? { visibility } : {})
    }),
  deleteCollabFolder: (id) => request('DELETE', `/collab/folders/${id}`),

  // ── 协作空间 文档 ───────────────────────────────────────
  // GET /collab/documents — scope/type/folder_id/keyword/related_user_id/modified_after/modified_before/page/page_size
  listCollabDocuments: (params = {}) =>
    request('GET', '/collab/documents', null, params),

  getCollabDocument: (id) => request('GET', `/collab/documents/${id}`),

  // 创建各类文档（table 可编辑；markdown/whiteboard/ppt/file 为占位，open_url 空串）
  createCollabDocument: (type, { folderId, name, visibility } = {}) =>
    request('POST', `/collab/documents/${type}`, {
      ...(folderId !== undefined ? { folderId } : {}),
      name,
      ...(visibility ? { visibility } : {})
    }),

  // Markdown 正文读写
  getCollabDocContent: (id) => request('GET', `/collab/documents/${id}/content`),
  putCollabDocContent: (id, { content, baseVersion }) =>
    request('PUT', `/collab/documents/${id}/content`, { content, baseVersion }),

  updateCollabDocument: (id, { name, folderId, visibility } = {}) =>
    request('PUT', `/collab/documents/${id}`, {
      ...(name !== undefined ? { name } : {}),
      ...(folderId !== undefined ? { folderId } : {}),
      ...(visibility !== undefined ? { visibility } : {})
    }),
  trashCollabDocument: (id) => request('DELETE', `/collab/documents/${id}`),
  restoreCollabDocument: (id) => request('POST', `/collab/documents/${id}/restore`, {}),
  permanentDeleteCollabDocument: (id) => request('DELETE', `/collab/documents/${id}/permanent`),
  // 清空当前用户的回收站（批量硬删）
  emptyCollabTrash: () => request('DELETE', '/collab/trash'),

  // ── 协作空间 星标 / 访问 / 打开 URL ────────────────────
  starCollabDocument: (id) => request('POST', `/collab/documents/${id}/star`, {}),
  unstarCollabDocument: (id) => request('DELETE', `/collab/documents/${id}/star`),
  recordCollabDocumentAccess: (id) => request('POST', `/collab/documents/${id}/access`, {}),
  getCollabDocumentOpenUrl: (id) => request('GET', `/collab/documents/${id}/open-url`),

  // ── 协作表格访问记录 ─────────────────────────────────
  getTableRecents: (params = {}) => request('GET', '/collab/table-recents', null, params),
  recordTableRecent: (url) => request('POST', '/collab/table-recents', { url }),
  removeTableRecent: (id) => request('DELETE', `/collab/table-recents/${id}`),
  clearTableRecents: () => request('DELETE', '/collab/table-recents'),

  // ── 协作空间 存储用量 ──────────────────────────────────
  getCollabStorage: () => request('GET', '/collab/storage'),

  // ── 协作空间 下载 / 复制 ────────────────────────────────
  // GET /collab/documents/{id}/download — 返回文件流，前端取 blob 走浏览器下载
  downloadCollabDocument: async (id) => {
    const token = getToken()
    const headers = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    headers['X-Frontend-Version'] = FRONTEND_VERSION
    const url = `${_API_HOST}/api/v1/collab/documents/${id}/download`
    const res = await fetch(url, { headers })
    if (!res.ok) throw new Error(`下载失败：HTTP ${res.status}`)
    const blob = await res.blob()
    // 从 Content-Disposition 提取文件名；失败兜底 id
    let filename = String(id)
    const cd = res.headers.get('Content-Disposition') || ''
    const mStar = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    const m = /filename="?([^";]+)"?/i.exec(cd)
    if (mStar) { try { filename = decodeURIComponent(mStar[1]) } catch { filename = mStar[1] } }
    else if (m) filename = m[1]
    return { blob, filename }
  },
  // POST /collab/documents/{id}/copy — 返回新文档完整对象
  copyCollabDocument: (id, { folderId } = {}) =>
    request('POST', `/collab/documents/${id}/copy`, folderId !== undefined ? { folderId } : {}),

  // ── 团队成员（协作页"创建人"筛选用） ────────────────
  listTeamMembers: ({ departmentId, keyword } = {}) =>
    request('GET', '/team/members', null, {
      ...(departmentId ? { departmentId } : {}),
      ...(keyword ? { keyword } : {})
    }),

  // ── 协作权限：协作者 6 接口 ────────────────────────
  listCollabDocCollaborators: (id) => request('GET', `/collab/documents/${id}/collaborators`),
  // 批量邀请 / upsert 角色：{ userIds: [], role: 'viewer'|'commenter'|'editor' }
  inviteCollabDocCollaborators: (id, { userIds, role }) =>
    request('POST', `/collab/documents/${id}/collaborators`, { userIds, role }),
  updateCollabDocCollaboratorRole: (id, userId, { role }) =>
    request('PUT', `/collab/documents/${id}/collaborators/${userId}`, { role }),
  removeCollabDocCollaborator: (id, userId) =>
    request('DELETE', `/collab/documents/${id}/collaborators/${userId}`),
  // 分享链接配置
  getCollabDocShareLink: (id) => request('GET', `/collab/documents/${id}/share-link`),
  // 分享链接 accept：匿名→协作者 upsert；token 是 path 参数（非 JWT）
  acceptCollabShareLink: (token) => request('POST', `/collab/share-links/${token}/accept`, {}),
  updateCollabDocShareLink: (id, { scope, defaultRole, enabled }) =>
    request('PUT', `/collab/documents/${id}/share-link`, {
      ...(scope !== undefined ? { scope } : {}),
      ...(defaultRole !== undefined ? { defaultRole } : {}),
      ...(enabled !== undefined ? { enabled } : {})
    }),

  // ── 文件上传（前端直传 OSS 三段式） ────────────────
  // Step 1: 拿 OSS 预签名 URL
  initCollabFileUpload: ({ name, folderId, mimeType, sizeBytes }) =>
    request('POST', '/collab/documents/file/upload-init', {
      name,
      ...(folderId !== undefined ? { folderId } : {}),
      mimeType,
      sizeBytes
    }),
  // Step 2: 直 PUT 到 OSS（不走 gateway，无 auth 头，Content-Type 必须和 init 时一致）
  putToOss: async (uploadUrl, file, contentType) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
      body: file
    })
    if (!res.ok) throw new Error(`OSS 上传失败: HTTP ${res.status}`)
    return true
  },
  // Step 3: 通知后端上传完成，后端拿 ossKey 落库
  completeCollabFileUpload: (id, { sizeBytes, mimeType } = {}) =>
    request('POST', `/collab/documents/${id}/upload-complete`, {
      ...(sizeBytes !== undefined ? { sizeBytes } : {}),
      ...(mimeType !== undefined ? { mimeType } : {})
    }),

  // ── 知识库 docmost / wiki 接口 (29 项) ─────────────────────
  // docmost 入口
  getDocmostSpace: () => request('GET', '/api/v1/docmost/space'),
  launchDocmostUrl: (to) =>
    request('POST', '/api/v1/docmost/launch-url', { to: to || '/' }),

  // wiki 元信息
  getWikiWorkspace: () => request('GET', '/api/v1/wiki/workspace'),

  // wiki spaces (v1.7) — 4 个 nav 都走这一个接口，按 query 切换：
  //   全部 / 与我共享：不传 / role=admin|editor|viewer|all
  //   最近使用：recent=true（按 lastActivityAt 降序，覆盖 sort）
  //   已加星标：starred=true
  //   回收站：trashed=true（需 workspace admin）
  // v1.9: folderId 过滤 — 传 uuid 只返该 folder 下的；传 'top' 只返顶层（用户未入文件夹的）
  listWikiSpaces: ({ page, limit, role, sort, related_user_id, modified_after, modified_before, recent, starred, trashed, folderId } = {}) =>
    request('GET', '/api/v1/wiki/spaces', null, {
      page: page || 1,
      limit: limit || 50,
      ...(role ? { role } : {}),
      ...(sort ? { sort } : {}),
      ...(related_user_id ? { related_user_id } : {}),
      ...(modified_after ? { modified_after } : {}),
      ...(modified_before ? { modified_before } : {}),
      ...(recent ? { recent: true } : {}),
      ...(starred ? { starred: true } : {}),
      ...(trashed ? { trashed: true } : {}),
      ...(folderId !== undefined && folderId !== null && folderId !== '' ? { folderId } : {})
    }),
  getWikiSpace: (spaceId) => request('GET', `/api/v1/wiki/spaces/${spaceId}`),
  getWikiSpaceMembers: (spaceId, { page, limit } = {}) =>
    request('GET', `/api/v1/wiki/spaces/${spaceId}/members`, null, {
      page: page || 1, limit: limit || 50
    }),
  // wiki spaces 写/管理
  createWikiSpace: (body) => request('POST', '/api/v1/wiki/spaces', body || {}),
  updateWikiSpace: (id, body) => request('PUT', `/api/v1/wiki/spaces/${id}`, body || {}),
  softDeleteWikiSpace: (id) => request('DELETE', `/api/v1/wiki/spaces/${id}`),
  permanentDeleteWikiSpace: (id) =>
    request('DELETE', `/api/v1/wiki/spaces/${id}`, null, { permanent: true }),
  getWikiSpaceTrash: ({ page, limit } = {}) =>
    request('GET', '/api/v1/wiki/spaces/trash', null, {
      page: page || 1, limit: limit || 50
    }),
  restoreWikiSpace: (id) => request('POST', `/api/v1/wiki/spaces/${id}/restore`),

  // v1.9 清空回收站（admin 清整 workspace，普通成员清自己 deletedById）
  clearWikiTrash: () => request('DELETE', '/api/v1/wiki/trash'),

  // v1.9 文件夹（per-user，每个用户自己的一棵树）
  listWikiFolders: () => request('GET', '/api/v1/wiki/folders'),
  createWikiFolder: ({ name, parentId, sortOrder } = {}) =>
    request('POST', '/api/v1/wiki/folders', {
      name,
      ...(parentId !== undefined ? { parentId } : {}),
      ...(sortOrder !== undefined ? { sortOrder } : {})
    }),
  updateWikiFolder: (folderId, { name, parentId, sortOrder } = {}) =>
    request('PUT', `/api/v1/wiki/folders/${folderId}`, {
      ...(name !== undefined ? { name } : {}),
      ...(parentId !== undefined ? { parentId } : {}),    // null 显式传是移到顶层
      ...(sortOrder !== undefined ? { sortOrder } : {})
    }),
  deleteWikiFolder: (folderId) => request('DELETE', `/api/v1/wiki/folders/${folderId}`),

  // wiki 协作者管理（二期 2026-05-11，v1.5 role enum 已统一为 admin/editor/viewer）
  addWikiSpaceMembers: ({ spaceId, role, userIds, groupIds }) =>
    request('POST', '/api/v1/wiki/spaces/members/add', {
      spaceId,
      role,                                    // 'admin' | 'editor' | 'viewer'
      ...(userIds && userIds.length ? { userIds } : {}),
      ...(groupIds && groupIds.length ? { groupIds } : {})
    }),
  removeWikiSpaceMember: ({ spaceId, userId, groupId }) =>
    request('POST', '/api/v1/wiki/spaces/members/remove', {
      spaceId,
      ...(userId ? { userId } : {}),
      ...(groupId ? { groupId } : {})
    }),
  // wiki 导出（二期 2026-05-11）
  // 直接返二进制流，不走 ARP envelope；返 { blob, filename }
  exportWikiPage: async ({ pageId, format, includeChildren, includeAttachments }) => {
    const token = getToken()
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    headers['X-Frontend-Version'] = FRONTEND_VERSION
    const res = await fetch(`${_API_HOST}/api/v1/wiki/pages/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        pageId,
        format: format || 'markdown',
        ...(includeChildren !== undefined ? { includeChildren } : {}),
        ...(includeAttachments !== undefined ? { includeAttachments } : {})
      })
    })
    if (!res.ok) throw new Error(`导出失败：HTTP ${res.status}`)
    const blob = await res.blob()
    let filename = pageId
    const cd = res.headers.get('Content-Disposition') || ''
    const mStar = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    const m = /filename="?([^";]+)"?/i.exec(cd)
    if (mStar) { try { filename = decodeURIComponent(mStar[1]) } catch (_) { filename = mStar[1] } }
    else if (m) filename = m[1]
    return { blob, filename }
  },
  exportWikiSpace: async ({ spaceId, format, includeAttachments }) => {
    const token = getToken()
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    headers['X-Frontend-Version'] = FRONTEND_VERSION
    const res = await fetch(`${_API_HOST}/api/v1/wiki/spaces/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        spaceId,
        format: format || 'markdown',
        ...(includeAttachments !== undefined ? { includeAttachments } : {})
      })
    })
    if (!res.ok) throw new Error(`导出失败：HTTP ${res.status}`)
    const blob = await res.blob()
    let filename = spaceId + '.zip'
    const cd = res.headers.get('Content-Disposition') || ''
    const mStar = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    const m = /filename="?([^";]+)"?/i.exec(cd)
    if (mStar) { try { filename = decodeURIComponent(mStar[1]) } catch (_) { filename = mStar[1] } }
    else if (m) filename = m[1]
    return { blob, filename }
  },

  // wiki 模板系统（二期 2026-05-09）
  // 列表 items 不含 content，详情才有；新建走 from-template 一体化建 space + 根 page
  listWikiTemplates: ({ spaceId, page, limit } = {}) =>
    request('GET', '/api/v1/wiki/templates', null, {
      ...(spaceId ? { spaceId } : {}),
      page: page || 1, limit: limit || 50
    }),
  getWikiTemplate: (templateId) => request('GET', `/api/v1/wiki/templates/${templateId}`),
  // 实际路径是 `/wiki/space-from-template`（单数），不是 spaces/from-template
  createWikiSpaceFromTemplate: ({ templateId, name, slug, description, cover }) =>
    request('POST', '/api/v1/wiki/space-from-template', {
      templateId,
      name,
      ...(slug ? { slug } : {}),
      ...(description ? { description } : {}),
      ...(cover ? { cover } : {})
    }),
  // wiki 分享链接（二期 2026-05-11，v1.5 字段补齐 scope / defaultRole / expireAt / 响应返 url）
  // 知识库分享统一基于 space（老 for-page / pageId 接口已废除）
  getWikiSpaceShare: (spaceId) =>
    request('GET', '/api/v1/wiki/shares/for-space', null, { spaceId }),
  // L 修：当前 ARP 用户在 docmost 体系里的身份桥接
  // 返 { arpUserId, docmostUserId, name, email, avatarUrl, workspaceId, workspaceRole }
  getMyWikiIdentity: () => request('GET', '/api/v1/wiki/me/identity'),
  // K 修：share 视角元数据接口，对 anyone-with-link 接收方开放，返 {share, page, space, creator, membership}
  getWikiShareByKey: (key) =>
    request('GET', `/api/v1/wiki/shares/by-key/${key}`),
  // 新版：访问 share + 自动 acceptShare（anyone-link 自动加为成员）
  // 返 { share, space, page, membership: { isMember:true, role, canEdit, canComment, canShare, canManageMembers }, justJoined }
  // 404 → 无权访问（restricted 非白名单 / 失效）；不应该再 iframe
  visitWikiShareByKey: (key) =>
    request('POST', `/api/v1/wiki/shares/by-key/${key}/visit`),
  // 分享人控制链接权限：defaultRole 'viewer' | 'editor'（后端已去掉整库 editor 的 400 守卫）
  createWikiSpaceShare: ({ spaceId, includeSubPages, searchIndexing, scope, defaultRole, expireAt }) =>
    request('POST', '/api/v1/wiki/shares', {
      spaceId,
      ...(includeSubPages !== undefined ? { includeSubPages } : {}),
      ...(searchIndexing !== undefined ? { searchIndexing } : {}),
      ...(scope ? { scope } : {}),                          // 'restricted' | 'anyone-with-link'
      ...(defaultRole ? { defaultRole } : {}),              // 'viewer' | 'editor'
      ...(expireAt !== undefined ? { expireAt } : {})       // ISO 或 null（永不过期）
    }),
  // PUT/DELETE 按 shareId，对整库 share 也适用
  updateWikiShare: (shareId, { includeSubPages, searchIndexing, scope, defaultRole, expireAt } = {}) =>
    request('PUT', `/api/v1/wiki/shares/${shareId}`, {
      ...(includeSubPages !== undefined ? { includeSubPages } : {}),
      ...(searchIndexing !== undefined ? { searchIndexing } : {}),
      ...(scope ? { scope } : {}),
      ...(defaultRole ? { defaultRole } : {}),
      ...(expireAt !== undefined ? { expireAt } : {})
    }),
  deleteWikiShare: (shareId) => request('DELETE', `/api/v1/wiki/shares/${shareId}`),
  // v1.6 — 外人凭 share token 接受邀请加入 space（必须先登录 ARP）
  // 幂等：已是 member 返 { alreadyMember: true, role }；未加入返 { alreadyMember: false, role: defaultRole }
  acceptWikiShare: (token) => request('POST', `/api/v1/wiki/shares/${token}/accept`, {}),

  // v1.6 — 复制知识库（整棵 page 树 + attachments）
  // body 可空（默认副本名 `${原名} (Copy)`）；返 { space, sourceSpaceId, pagesCopied }
  copyWikiSpace: (spaceId, { name } = {}) =>
    request('POST', `/api/v1/wiki/spaces/${spaceId}/copy`, name ? { name } : {}),

  // v1.6 — 上传单文件 → page（30MB 上限，支持 .md/.html/.docx/.pdf）
  // 返新建 page 完整对象。注意：总建到 space 顶层，要调位走 pages/move
  importWikiFile: async (spaceId, file) => {
    const token = getToken()
    const headers = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    headers['X-Frontend-Version'] = FRONTEND_VERSION
    const fd = new FormData()
    fd.append('file', file)
    const res = await fetch(`${_API_HOST}/api/v1/wiki/spaces/${spaceId}/import-file`, {
      method: 'POST', headers, body: fd
    })
    const json = await res.json()
    if (!res.ok || (json.code != null && json.code !== 200)) {
      throw new Error(json.message || `导入失败：HTTP ${res.status}`)
    }
    return json.data || json
  },

  // v1.6 — 上传 zip → 嵌套 pages（200MB 上限，异步任务）
  // source: 'generic' | 'notion' | 'confluence'，默认 generic
  // 返 { id, status, fileName, fileSize }，status='processing' 时轮询
  importWikiZip: async (spaceId, file, { source = 'generic' } = {}) => {
    const token = getToken()
    const headers = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    headers['X-Frontend-Version'] = FRONTEND_VERSION
    const fd = new FormData()
    fd.append('file', file)
    fd.append('source', source)
    const res = await fetch(`${_API_HOST}/api/v1/wiki/spaces/${spaceId}/import-zip`, {
      method: 'POST', headers, body: fd
    })
    const json = await res.json()
    if (!res.ok || (json.code != null && json.code !== 200)) {
      throw new Error(json.message || `导入失败：HTTP ${res.status}`)
    }
    return json.data || json
  },

  changeWikiSpaceMemberRole: ({ spaceId, userId, groupId, role }) =>
    request('POST', '/api/v1/wiki/spaces/members/change-role', {
      spaceId,
      role,                                    // 'admin' | 'member' | 'viewer'
      ...(userId ? { userId } : {}),
      ...(groupId ? { groupId } : {})
    }),

  // wiki pages
  getWikiPage: (pageId) => request('GET', `/api/v1/wiki/page/${pageId}`),
  getWikiPagesSidebar: ({ spaceId, pageId, page, limit } = {}) =>
    request('GET', '/api/v1/wiki/pages/sidebar', null, {
      ...(spaceId ? { spaceId } : {}),
      ...(pageId ? { pageId } : {}),
      page: page || 1, limit: limit || 50
    }),
  getWikiRecentPages: ({ spaceId, page, limit } = {}) =>
    request('GET', '/api/v1/wiki/pages/recent', null, {
      ...(spaceId ? { spaceId } : {}),
      page: page || 1, limit: limit || 50
    }),
  getWikiCreatedByMe: ({ spaceId, page, limit } = {}) =>
    request('GET', '/api/v1/wiki/pages/created-by-me', null, {
      ...(spaceId ? { spaceId } : {}),
      page: page || 1, limit: limit || 50
    }),
  // wiki pages 写/管理
  createWikiPage: (body) => request('POST', '/api/v1/wiki/pages', body || {}),
  // 注意：PUT pages 只改 title/icon，正文走 docmost iframe Yjs 实时编辑
  updateWikiPage: (id, body) => request('PUT', `/api/v1/wiki/pages/${id}`, body || {}),
  softDeleteWikiPage: (id) => request('DELETE', `/api/v1/wiki/pages/${id}`),
  // v1.6 — 章节永久删（需 space admin），跟 space permanent 同 pattern
  permanentDeleteWikiPage: (id) =>
    request('DELETE', `/api/v1/wiki/pages/${id}`, null, { permanent: true }),
  moveWikiPage: (id, body) => request('POST', `/api/v1/wiki/pages/${id}/move`, body || {}),
  getWikiPageTrash: ({ page, limit } = {}) =>
    request('GET', '/api/v1/wiki/trash', null, {
      page: page || 1, limit: limit || 50
    }),
  restoreWikiPage: (id) => request('POST', `/api/v1/wiki/trash/${id}/restore`),

  // wiki storage （sidebar 进度条）
  getWikiStorage: () => request('GET', '/api/v1/wiki/storage'),

  // wiki favorites
  getWikiFavorites: ({ type, spaceId, page, limit } = {}) =>
    request('GET', '/api/v1/wiki/favorites', null, {
      ...(type ? { type } : {}),
      ...(spaceId ? { spaceId } : {}),
      page: page || 1, limit: limit || 50
    }),
  addWikiFavorite: ({ type, pageId, spaceId, templateId } = {}) =>
    request('POST', '/api/v1/wiki/favorites', {
      type,
      ...(pageId ? { pageId } : {}),
      ...(spaceId ? { spaceId } : {}),
      ...(templateId ? { templateId } : {})
    }),
  removeWikiFavorite: ({ type, pageId, spaceId, templateId } = {}) =>
    request('POST', '/api/v1/wiki/favorites/remove', {
      type,
      ...(pageId ? { pageId } : {}),
      ...(spaceId ? { spaceId } : {}),
      ...(templateId ? { templateId } : {})
    }),
  getWikiFavoriteIds: ({ type, spaceId } = {}) =>
    request('GET', '/api/v1/wiki/favorites/ids', null, {
      ...(type ? { type } : {}),
      ...(spaceId ? { spaceId } : {})
    }),

  // wiki search
  searchWiki: ({ q, spaceId, limit } = {}) =>
    request('GET', '/api/v1/wiki/search', null, {
      q,
      ...(spaceId ? { spaceId } : {}),
      limit: limit || 50
    }),
  suggestWiki: (q) =>
    request('GET', '/api/v1/wiki/search/suggest', null, { q })
}
